import * as cryptoApi from "app/api/cryptoApi";
import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import { getRoutePathForView } from "app/helpers/routerPathHelper";
import ManageFundingDepositDetails from "app/screens/ManageFundingScreen/components/ManageFundingDepositDetails/ManageFundingDepositDetails";
import { UserType } from "app/types/userType";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CryptoFundingRequestQuote from "../CryptoFundingRequestQuote/CryptoFundingRequestQuote";
import CryptoFundingCompleteDeposit from "./components/CryptoFundingCompleteDeposit/CryptoFundingCompleteDeposit";
import "./CryptoFundingDeposit.scss";

function CryptoFundingDeposit(props: { user: UserType }) {
  const { user } = props;
  const history = useHistory();
  let [intervalId, setIntervalId] = useState<any>();
  const [formLoading, setFormLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState("");
  const [amountSending, setAmountSending] = useState(0);
  const [deposit_address, setDepositAddress] = useState("");
  const [receiveQuantity, setReceiveQuantity] = useState(null);
  const [expireTime, setExpireTime] = useState<any>(null);
  const [status, setStatus] = useState<any>(null);
  const [transactionId, setTransactionId] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [txHash, setTxHash] = useState(null);
  const [depositDetails, setDepositDetails] = useState({
    amountSending: amountSending,
    receiveQuantity: receiveQuantity,
    expireTime: expireTime,
    status: status,
    txHash: txHash,
    transactionId: transactionId,
    deposit_address: deposit_address,
    confirmed: confirmed,
    selectedCoin: selectedCoin,
  });
  const [poll, setPoll] = useState(false);

  useEffect(() => {
    // check for withdrawal Details
    checkLocalStorage();
    // clean up
    return () => {
      if (status === "SUCCESS" || status === "REJECT") {
        cancelDeposit();
      } else {
        clearInterval(intervalId);
        setPoll(false);
      }
    };
  }, []);

  useEffect(() => {
    //
    if (transactionId) {
      pollDepositStatus();
      if (status) {
        if (status === "REJECT") setPoll(false);
        saveDepositDetails();
      }
    }
    // if confirm deposit selected
    if (confirmed) saveDepositDetails();
  }, [transactionId, status, confirmed]);

  const checkLocalStorage = () => {
    const localStorageData = JSON.parse(
      localStorage.getItem(`3et-crypto-deposit-details-${user["userId"]}`)
    );

    if (localStorageData !== null) {
      setAmountSending(localStorageData.amountSending);
      setReceiveQuantity(localStorageData.receiveQuantity);
      setExpireTime(localStorageData.expireTime);
      setStatus(localStorageData.status);
      setDepositAddress(localStorageData.deposit_address);
      setTxHash(localStorageData.txHash);
      setTransactionId(localStorageData.transactionId);
      setSelectedCoin(localStorageData.selectedCoin);
      setConfirmed(localStorageData.confirmed);
    }
  };

  const saveDepositDetails = (resp?: any) => {
    localStorage.setItem(
      `3et-crypto-deposit-details-${user["userId"]}`,
      JSON.stringify({
        amountSending: amountSending,
        receiveQuantity: receiveQuantity,
        expireTime: expireTime,
        status: status,
        txHash: txHash,
        transactionId: transactionId,
        deposit_address: deposit_address,
        confirmed: confirmed,
        selectedCoin: selectedCoin,
      })
    );

    setDepositDetails({
      amountSending: amountSending,
      receiveQuantity: receiveQuantity,
      expireTime: expireTime,
      status: status,
      txHash: txHash,
      transactionId: transactionId,
      deposit_address: deposit_address,
      confirmed: confirmed,
      selectedCoin: selectedCoin,
    });
  };

  const pollDepositStatus = () => {
    getTransactionStatus();
    setPoll(true);
  };

  useEffect(() => {
    const intervalId: any =
      poll &&
      setInterval(() => {
        if (!poll) clearInterval(intervalId);
        getTransactionStatus();
      }, 5000);
    return () => clearInterval(intervalId);
  }, [poll]);

  const getTransactionStatus = async () => {
    if (transactionId) {
      cryptoApi.pollStatus(transactionId).then(resp => {
        setStatus(resp.data.status);
        setTxHash(resp.data.txHash);
      });
    }
  };

  const onSelectCoin = item => setSelectedCoin(item.value);

  const onChangeAmount = (id: any, value: number) => setAmountSending(value);

  const requestQuote = () => {
    setQuoteLoading(true);
    setQuoteError("");
    setFormLoading(true);

    cryptoApi
      .requestDepositQuote(selectedCoin, user["currencyCode"], amountSending)
      .then(resp => {
        // no idea why you would do this???
        resp.data.amount = amountSending;
        setQuoteLoading(false);
        setDepositAddress(resp.data["deposit_address"]);
        setReceiveQuantity(resp.data.quantity);
        setExpireTime(resp.data.expiryTime);
        setTransactionId(resp.data.transactionId);
        setFormLoading(false);
      })
      .then(() => {
        pollDepositStatus();
      })
      .catch(error => {
        let errorMessage = "";
        if (error.response) {
          if (
            error.response.data.errors.length > 0 &&
            error.response.data.errors[0].messages
          ) {
            errorMessage = error.response.data.errors[0].messages[0];
          } else if (
            error.response.data.errors.length > 0 &&
            error.response.data.errors[0].detail
          ) {
            errorMessage = error.response.data.errors[0].detail;
          }
        }
        setQuoteLoading(false);
        setFormLoading(false);
        setQuoteError(errorMessage);
      });
  };

  const completeQuote = () => {
    setConfirmed(true);
  };

  const cancelDeposit = () => {
    setReceiveQuantity(null);
    setExpireTime(null);
    setStatus(null);
    setTransactionId(null);
    setDepositAddress("");
    setConfirmed(false);
    setAmountSending(0);
    setPoll(false);
    setSelectedCoin("");
    //
    localStorage.removeItem(`3et-crypto-deposit-details-${user["userId"]}`);
    intervalId = clearInterval(intervalId);

    setIntervalId(null);
    setDepositDetails({
      amountSending: 0,
      receiveQuantity: null,
      expireTime: null,
      status: null,
      txHash: null,
      transactionId: null,
      deposit_address: "",
      confirmed: false,
      selectedCoin: "",
    });
  };

  const backToHomepage = () => {
    const path = getRoutePathForView();
    history.push(path);
    // history.push("/manageFunding");
  };

  const copyAmountInfo = () => {
    if (receiveQuantity) {
      navigator.clipboard.writeText(receiveQuantity.toString()).then(() => {
        console.log("Amount added to clipboard: " + receiveQuantity);
      });
    }
  };

  const copyWalletAddress = () => {
    if (deposit_address) {
      navigator.clipboard.writeText(deposit_address).then(() => {
        console.log("Wallet Address copied to clipboard: " + deposit_address);
      });
    }
  };

  if (formLoading) return <LoadingSpinner />;

  return (
    <div className="crypto-funding-deposit">
      <ManageFundingDepositDetails user={user} />
      <div className="crypto-funding-deposit__content">
        {depositDetails.receiveQuantity ? (
          <CryptoFundingCompleteDeposit
            depositDetails={depositDetails}
            completeQuote={completeQuote}
            cancelDeposit={cancelDeposit}
            user={user}
            currency={depositDetails.selectedCoin}
            backToHomepage={backToHomepage}
            copyAmountInfo={copyAmountInfo}
            copyWalletAddress={copyWalletAddress}
            txHash={txHash}
          />
        ) : (
          <CryptoFundingRequestQuote
            type="DEPOSIT"
            onSelectCoin={onSelectCoin}
            onChangeAmount={onChangeAmount}
            requestQuote={requestQuote}
            // selectedCoin={depositDetails.selectedCoin}
            selectedCoin={selectedCoin}
            user={user}
            quoteError={quoteError}
            quoteLoading={quoteLoading}
            amountSending={amountSending}
          />
        )}
      </div>
    </div>
  );
}

export default CryptoFundingDeposit;
